import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { 
  RotateCw, 
  Paperclip, 
  SendHorizontal, 
  CheckCheck, 
  FileText, 
  Image as ImageIcon, 
  FileSpreadsheet, 
  Download, 
  Sparkles, 
  ShieldCheck, 
  AlertCircle, 
  X, 
  MessageSquare 
} from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import chatService from '../../services/chat.service';
import type { ChatMessage } from '../../types';
import { parseServerDate } from '../../utils/serverDate';

interface ApplicationChatProps {
    applicationId: number;
    isExpanded?: boolean;
    onToggle?: () => void;
    className?: string;
}

const REFRESH_INTERVAL = 60000; // 1 minute auto-refresh

export default function ApplicationChat({ applicationId, isExpanded = true, onToggle, className = '' }: ApplicationChatProps) {
    const { language } = useI18n();

    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Auto-scroll to bottom when new messages arrive
    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    // Load messages function
    const loadMessages = useCallback(async (showLoading = false) => {
        if (showLoading) setIsLoading(true);
        setError(null);

        try {
            const allMessages = await chatService.loadAllMessages(applicationId);
            setMessages(allMessages);
            setLastRefresh(new Date());
        } catch (err) {
            console.error('Failed to load messages:', err);
            setError(language === 'ar' ? 'فشل تحميل الرسائل' : 'Failed to load messages');
        } finally {
            setIsLoading(false);
        }
    }, [applicationId, language]);

    // Initial load and auto-refresh setup
    useEffect(() => {
        loadMessages(true);

        // Set up auto-refresh every 1 minute
        refreshIntervalRef.current = setInterval(() => {
            loadMessages(false);
        }, REFRESH_INTERVAL);

        return () => {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
            }
        };
    }, [loadMessages]);

    const handleSendMessage = async () => {
        const trimmed = inputMessage.trim();
        if (!trimmed || isSending) return;

        setIsSending(true);
        setError(null);

        try {
            const newMessage = await chatService.sendTextMessage(applicationId, trimmed);
            setMessages(prev => [...prev, newMessage]);
            setInputMessage('');
            inputRef.current?.focus();
        } catch (err) {
            console.error('Failed to send message:', err);
            setError(language === 'ar' ? 'فشل إرسال الرسالة' : 'Failed to send message');
        } finally {
            setIsSending(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Check file size (20 MB limit)
        if (file.size > 20 * 1024 * 1024) {
            setError(language === 'ar' ? 'حجم الملف يجب ألا يتجاوز 20 ميجابايت' : 'File size must not exceed 20MB');
            return;
        }

        setIsUploading(true);
        setError(null);

        try {
            const newMessage = await chatService.sendFileMessage(applicationId, file);
            setMessages(prev => [...prev, newMessage]);
        } catch (err) {
            console.error('Failed to upload file:', err);
            setError(language === 'ar' ? 'فشل رفع الملف' : 'Failed to upload file');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleManualRefresh = () => {
        loadMessages(false);
    };

    const formatTime = (dateString: string) => {
        const date = parseServerDate(dateString);
        if (!date) return '';
        return date.toLocaleTimeString(language === 'ar' ? 'ar-EG' : 'en-US', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDate = (dateString: string) => {
        const date = parseServerDate(dateString);
        if (!date) return '';
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (date.toDateString() === today.toDateString()) {
            return language === 'ar' ? 'اليوم' : 'Today';
        } else if (date.toDateString() === yesterday.toDateString()) {
            return language === 'ar' ? 'أمس' : 'Yesterday';
        }
        return date.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', {
            month: 'short',
            day: 'numeric',
            year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
        });
    };

    const formatLastRefresh = () => {
        const now = new Date();
        const diffMs = now.getTime() - lastRefresh.getTime();
        const diffSecs = Math.floor(diffMs / 1000);
        const diffMins = Math.floor(diffSecs / 60);

        if (diffSecs < 60) {
            return language === 'ar' ? 'الآن' : 'Just now';
        }
        return language === 'ar' ? `منذ ${diffMins} دقيقة` : `${diffMins}m ago`;
    };

    const groupMessagesByDate = (messages: ChatMessage[]) => {
        const groups: { date: string; messages: ChatMessage[] }[] = [];
        let currentDate = '';

        messages.forEach(message => {
            const messageDate = parseServerDate(message.created_at)?.toDateString() ?? '';
            if (messageDate !== currentDate) {
                currentDate = messageDate;
                groups.push({ date: message.created_at, messages: [message] });
            } else {
                groups[groups.length - 1].messages.push(message);
            }
        });

        return groups;
    };

    const getSenderTypeLabel = (senderType: string) => {
        const labels: Record<string, { en: string; ar: string }> = {
            student: { en: 'You', ar: 'أنت' },
            consultant: { en: 'Consultant', ar: 'المستشار' },
            admin: { en: 'Admin', ar: 'المسؤول' }
        };
        const label = labels[senderType] || { en: senderType, ar: senderType };
        return language === 'ar' ? label.ar : label.en;
    };

    const getSenderColor = (senderType: string) => {
        switch (senderType) {
            case 'consultant':
                return 'bg-gradient-to-br from-[#5B3FD0] to-[#7B5FE0]';
            case 'admin':
                return 'bg-gradient-to-br from-[#FF9500] to-[#FFB347]';
            default:
                return 'bg-gradient-to-br from-[#007AFF] to-[#5AC8FA]';
        }
    };

    const renderFileIcon = (fileType: string | null) => {
        if (!fileType) return <Paperclip size={18} className="text-[#5B3FD0]" />;
        if (fileType.startsWith('image/')) return <ImageIcon size={18} className="text-[#7c3aed]" />;
        if (fileType.includes('pdf')) return <FileText size={18} className="text-[#dc2626]" />;
        if (fileType.includes('word') || fileType.includes('document')) return <FileText size={18} className="text-[#2563eb]" />;
        if (fileType.includes('excel') || fileType.includes('spreadsheet')) return <FileSpreadsheet size={18} className="text-[#16a34a]" />;
        return <Paperclip size={18} className="text-[#5B3FD0]" />;
    };

    const formatFileSize = (bytes: number | null) => {
        if (!bytes) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const isOwnMessage = (message: ChatMessage) => {
        return message.sender_type === 'student';
    };

    const isImageFile = (fileType: string | null) => {
        return fileType?.startsWith('image/');
    };

    const quickQuestions = language === 'ar' ? [
        'ما هي الخطوة القادمة لطلبي؟',
        'هل تم فحص أوراقي والموافقة عليها؟',
        'أريد ترشيحات لأفضل الجامعات المناسبة لتخصصي'
    ] : [
        'What is the next step for my application?',
        'Have my documents been reviewed?',
        'Can you recommend top universities for my major?'
    ];

    const handleSelectQuickQuestion = (q: string) => {
        setInputMessage(q);
        inputRef.current?.focus();
    };

    const renderTextWithLinks = (text: string, isOwn: boolean): ReactNode[] => {
        const urlPattern = /((https?:\/\/|www\.)[^\s<]+)/gi;
        const parts = text.split(urlPattern);

        return parts.map((part, index) => {
            const isUrl = /^(https?:\/\/|www\.)[^\s<]+$/i.test(part);
            if (!isUrl) {
                return <span key={`text-${index}`}>{part}</span>;
            }

            const href = part.startsWith('http') ? part : `https://${part}`;
            return (
                <a
                    key={`link-${index}`}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`underline break-all transition-opacity ${isOwn ? 'text-white hover:text-white/85' : 'text-[#5B3FD0] hover:text-[#4B32B3]'}`}
                >
                    {part}
                </a>
            );
        });
    };

    if (!isExpanded) {
        return (
            <button
                onClick={onToggle}
                className="fixed bottom-6 right-6 rtl:right-auto rtl:left-6 w-16 h-16 bg-gradient-to-br from-[#5B3FD0] to-[#7B5FE0] hover:from-[#4B32B3] hover:to-[#6B4FD0] text-white rounded-2xl shadow-xl shadow-[#5B3FD0]/30 flex items-center justify-center transition-all hover:scale-105 active:scale-95 z-50 group cursor-pointer"
                title={language === 'ar' ? 'اتكلم معانا مباشره' : 'Chat with Us Directly'}
            >
                <MessageSquare size={26} className="transition-transform group-hover:scale-110" />
                {messages.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white text-xs font-extrabold rounded-full flex items-center justify-center shadow-md border-2 border-white">
                        {messages.length > 99 ? '99+' : messages.length}
                    </span>
                )}
            </button>
        );
    }

    return (
        <div className={`bg-white rounded-3xl border border-[#dfd4f4] flex flex-col shadow-sm overflow-hidden h-full flex-1 ${className}`}>
            {/* Chat Header */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-3.5 border-b border-[#ece6f5] bg-gradient-to-r from-[#faf8fd] via-white to-[#faf8fd] flex-shrink-0">
                <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#5B3FD0] to-[#7B5FE0] text-white flex items-center justify-center shadow-sm flex-shrink-0">
                        <MessageSquare size={20} />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h3 className="font-extrabold text-[#1f192d] text-sm sm:text-base leading-tight">
                                {language === 'ar' ? 'مستشارك الأكاديمي المباشر' : 'Direct Academic Counselor'}
                            </h3>
                            <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold text-[#5B3FD0] bg-[#5B3FD0]/10 px-2 py-0.5 rounded-full">
                                <ShieldCheck size={11} />
                                {language === 'ar' ? 'معتمد' : 'Verified'}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-[#716588] mt-0.5">
                            <span className="inline-flex items-center gap-1 text-[#047857] font-semibold">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#10b981] animate-pulse" />
                                {language === 'ar' ? 'متصل الآن' : 'Active'}
                            </span>
                            <span className="text-gray-300">•</span>
                            <span>{language === 'ar' ? 'آخر تحديث: ' : 'Updated: '}{formatLastRefresh()}</span>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <span className="text-xs font-extrabold text-[#5B3FD0] bg-[#5B3FD0]/10 border border-[#5B3FD0]/20 px-2.5 py-1 rounded-full">
                        #{applicationId}
                    </span>

                    <button
                        onClick={handleManualRefresh}
                        disabled={isLoading}
                        className="p-2 hover:bg-[#f3edf9] text-[#716588] hover:text-[#5B3FD0] disabled:opacity-50 rounded-xl transition-colors cursor-pointer"
                        title={language === 'ar' ? 'تحديث الرسائل' : 'Refresh messages'}
                    >
                        <RotateCw size={17} className={isLoading ? 'animate-spin' : ''} />
                    </button>

                    {onToggle && (
                        <button
                            onClick={onToggle}
                            className="p-2 hover:bg-gray-100 text-[#716588] hover:text-gray-900 rounded-xl transition-colors cursor-pointer"
                            title={language === 'ar' ? 'إغلاق' : 'Close'}
                        >
                            <X size={18} />
                        </button>
                    )}
                </div>
            </div>

            {/* Error Banner */}
            {error && (
                <div className="px-4 py-2.5 bg-red-50 border-b border-red-100 flex-shrink-0 flex items-center justify-between">
                    <p className="text-xs sm:text-sm text-red-600 flex items-center gap-2">
                        <AlertCircle size={16} className="flex-shrink-0" />
                        <span>{error}</span>
                    </p>
                    <button 
                        onClick={() => setError(null)}
                        className="text-red-400 hover:text-red-600 p-1 cursor-pointer"
                    >
                        <X size={15} />
                    </button>
                </div>
            )}

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-3 bg-gradient-to-b from-[#faf8fd] via-white to-[#faf8fd] custom-scrollbar min-h-0">
                {isLoading ? (
                    <div className="flex items-center justify-center h-full py-12">
                        <div className="text-center">
                            <div className="w-10 h-10 border-3 border-[#5B3FD0] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                            <p className="text-[#716588] text-xs sm:text-sm font-semibold">
                                {language === 'ar' ? 'جاري تحميل الرسائل...' : 'Loading messages...'}
                            </p>
                        </div>
                    </div>
                ) : messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full py-10">
                        <div className="text-center max-w-md px-4">
                            <div className="w-16 h-16 bg-gradient-to-br from-[#5B3FD0]/15 to-[#5B3FD0]/5 text-[#5B3FD0] rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-sm">
                                <MessageSquare size={30} />
                            </div>
                            <h4 className="font-extrabold text-[#1f192d] text-lg mb-1.5">
                                {language === 'ar' ? 'اتكلم معانا مباشره' : 'Start the Conversation'}
                            </h4>
                            <p className="text-xs sm:text-sm text-[#716588] leading-relaxed mb-5">
                                {language === 'ar'
                                    ? 'مستشارك الأكاديمي جاهز للإجابة على جميع استفساراتك حول خطوات التقديم والجامعات والمستندات المطلوبة.'
                                    : 'Your academic counselor is ready to assist you with every step of your application, universities, and documents.'}
                            </p>

                            <div className="flex flex-col gap-2 text-start">
                                <span className="text-[11px] font-bold text-[#8c829e] uppercase tracking-wider text-center">
                                    {language === 'ar' ? 'أسئلة مقترحة للبدء:' : 'Suggested questions to start:'}
                                </span>
                                {quickQuestions.map((q, idx) => (
                                    <button
                                        key={idx}
                                        type="button"
                                        onClick={() => handleSelectQuickQuestion(q)}
                                        className="px-3.5 py-2.5 rounded-xl bg-white border border-[#e5def0] hover:border-[#5B3FD0] hover:bg-[#faf8fd] text-xs font-semibold text-[#2c233c] text-start transition-all shadow-2xs hover:translate-y-[-1px] cursor-pointer flex items-center justify-between gap-2 group"
                                    >
                                        <span>{q}</span>
                                        <Sparkles size={14} className="text-[#8c829e] group-hover:text-[#5B3FD0] flex-shrink-0 transition-colors" />
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    groupMessagesByDate(messages).map((group, groupIndex) => (
                        <div key={groupIndex}>
                            {/* Date Separator */}
                            <div className="flex items-center justify-center my-4">
                                <div className="h-px bg-gradient-to-r from-transparent via-[#e4dcf1] to-transparent flex-1" />
                                <span className="px-3.5 py-1 bg-white border border-[#e8e0f3] rounded-full text-[11px] text-[#716588] font-bold shadow-2xs">
                                    {formatDate(group.date)}
                                </span>
                                <div className="h-px bg-gradient-to-r from-transparent via-[#e4dcf1] to-transparent flex-1" />
                            </div>

                            {/* Messages */}
                            {group.messages.map((message, msgIndex) => {
                                const isOwn = isOwnMessage(message);
                                const showSender = !isOwn && (msgIndex === 0 || group.messages[msgIndex - 1].sender_type !== message.sender_type);
                                
                                return (
                                    <div
                                        key={message.id}
                                        className={`flex mb-3 ${isOwn ? 'justify-end' : 'justify-start'}`}
                                    >
                                        <div className={`max-w-[85%] sm:max-w-[75%] ${isOwn ? 'order-2' : ''}`}>
                                            {/* Sender Info for Incoming */}
                                            {showSender && (
                                                <div className="flex items-center gap-2 mb-1.5 px-1">
                                                    <div className={`w-5 h-5 rounded-md ${getSenderColor(message.sender_type)} text-white flex items-center justify-center text-[9px] font-bold shadow-xs`}>
                                                        {message.sender_name.charAt(0).toUpperCase()}
                                                    </div>
                                                    <span className="text-xs font-bold text-[#1f192d]">{message.sender_name}</span>
                                                    <span className="text-[10px] px-2 py-0.5 bg-[#f3edf9] text-[#5B3FD0] rounded-full font-bold">
                                                        {getSenderTypeLabel(message.sender_type)}
                                                    </span>
                                                </div>
                                            )}

                                            {/* Bubble Container */}
                                            <div
                                                className={`px-4 py-3 shadow-xs transition-shadow ${
                                                    isOwn
                                                        ? 'bg-gradient-to-br from-[#5B3FD0] to-[#7656E6] text-white rounded-2xl rounded-br-xs rtl:rounded-br-2xl rtl:rounded-bl-xs shadow-md shadow-[#5B3FD0]/15'
                                                        : 'bg-white border border-[#e7dff2] text-[#1f192d] rounded-2xl rounded-bl-xs rtl:rounded-bl-2xl rtl:rounded-br-xs shadow-xs'
                                                }`}
                                            >
                                                {message.message_type === 'text' ? (
                                                    <p className="whitespace-pre-wrap break-words text-[13.5px] sm:text-[14px] leading-relaxed">
                                                        {renderTextWithLinks(message.text_content || '', isOwn)}
                                                    </p>
                                                ) : (
                                                    <div>
                                                        {/* Image Preview */}
                                                        {isImageFile(message.file_type) && message.file_url && (
                                                            <a
                                                                href={message.file_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="block mb-2 overflow-hidden rounded-xl group"
                                                            >
                                                                <img
                                                                    src={message.file_url}
                                                                    alt={message.file_name || 'Image'}
                                                                    className="max-w-full max-h-56 rounded-xl object-cover border border-white/20 transition-transform duration-200 group-hover:scale-[1.02]"
                                                                    loading="lazy"
                                                                />
                                                            </a>
                                                        )}
                                                        {/* File Card */}
                                                        <a
                                                            href={message.file_url || '#'}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className={`flex items-center gap-3 p-2.5 rounded-xl transition-all ${
                                                                isOwn
                                                                    ? 'bg-white/12 hover:bg-white/20 text-white'
                                                                    : 'bg-[#f7f4fb] hover:bg-[#f0eaf7] text-[#1f192d] border border-[#ece4f5]'
                                                            }`}
                                                        >
                                                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isOwn ? 'bg-white/20' : 'bg-white shadow-2xs'}`}>
                                                                {renderFileIcon(message.file_type)}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-xs sm:text-sm font-bold truncate">
                                                                    {message.file_name}
                                                                </p>
                                                                <p className={`text-[10px] ${isOwn ? 'text-white/70' : 'text-[#716588]'}`}>
                                                                    {formatFileSize(message.file_size)}
                                                                </p>
                                                            </div>
                                                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${isOwn ? 'bg-white/20 text-white' : 'bg-[#5B3FD0]/10 text-[#5B3FD0]'}`}>
                                                                <Download size={14} />
                                                            </div>
                                                        </a>
                                                    </div>
                                                )}

                                                {/* Timestamp inside bubble */}
                                                <div className={`flex items-center gap-1.5 mt-1 pt-1 justify-end ${
                                                    isOwn ? 'text-white/80' : 'text-[#8c829e]'
                                                }`}>
                                                    <span className="text-[10px] font-medium leading-none">
                                                        {formatTime(message.created_at)}
                                                    </span>
                                                    {isOwn && <CheckCheck size={13} className="opacity-90 leading-none" />}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="px-3 sm:px-5 py-3 border-t border-[#ede7f5] bg-white flex-shrink-0">
                <div className="flex items-end gap-2 bg-[#fbf9fd] border border-[#dfd4f4] focus-within:border-[#5B3FD0] focus-within:ring-3 focus-within:ring-[#5B3FD0]/15 rounded-2xl sm:rounded-3xl p-1.5 transition-all shadow-2xs">
                    {/* File Upload Button */}
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        className="hidden"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.xls,.xlsx"
                    />
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                        className="p-2.5 sm:p-3 text-[#716588] hover:text-[#5B3FD0] hover:bg-white rounded-xl sm:rounded-2xl transition-all disabled:opacity-50 cursor-pointer flex-shrink-0"
                        title={language === 'ar' ? 'إرفاق ملف' : 'Attach file'}
                    >
                        {isUploading ? (
                            <div className="w-5 h-5 border-2 border-[#5B3FD0] border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <Paperclip size={19} />
                        )}
                    </button>

                    {/* Message Input */}
                    <textarea
                        ref={inputRef}
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={language === 'ar' ? 'اكتب رسالتك هنا... (اضغط Enter للإرسال)' : 'Type your message here... (Press Enter to send)'}
                        disabled={isSending}
                        rows={1}
                        className="flex-1 bg-transparent border-0 focus:outline-none focus:ring-0 text-[#1f192d] placeholder:text-[#978da9] text-xs sm:text-sm py-2 px-1 resize-none max-h-32 min-h-[38px] leading-relaxed outline-none"
                    />

                    {/* Send Button */}
                    <button
                        type="button"
                        onClick={handleSendMessage}
                        disabled={isSending || !inputMessage.trim() || inputMessage.length > 5000}
                        className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl sm:rounded-2xl bg-gradient-to-br from-[#5B3FD0] to-[#7656E6] hover:from-[#4B32B3] hover:to-[#6846de] disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center transition-all hover:scale-105 active:scale-95 shadow-sm shadow-[#5B3FD0]/25 flex-shrink-0 cursor-pointer"
                        title={language === 'ar' ? 'إرسال' : 'Send'}
                    >
                        {isSending ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <SendHorizontal size={18} className="rtl:rotate-180" />
                        )}
                    </button>
                </div>

                {/* Microcopy footer */}
                <div className="flex items-center justify-between px-2 pt-1.5 text-[10.5px] text-[#978da9]">
                    <span>
                        {language === 'ar' ? 'الحد الأقصى للملف: 10 ميجابايت · Shift + Enter لسطر جديد' : 'Max file: 10MB · Shift + Enter for new line'}
                    </span>
                    {inputMessage.length > 4000 && (
                        <span className={inputMessage.length > 5000 ? 'text-red-500 font-bold' : 'text-amber-500'}>
                            {inputMessage.length}/5000
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}
